import { Tooltip, TooltipProps } from '@nextui-org/react';
import styled from 'styled-components';

import ZIndexLayers from '../../services/ZIndexLayers';
import { ThemeProp } from '../../theme/Themes';

export interface ExtendedTooltipProps extends Partial<TooltipProps> {
  $zIndex?: number;
  $noMarginTop?: boolean;
  /**
   * @description Should receive a valid CSS string.
   * @example "1rem"
   */
  $maxWidth?: string;
  /**
   * @description Should receive a valid CSS string.
   * @example "1rem"
   */
  $minWidth?: string;
  /**
   * @description Should receive a valid CSS string.
   * @example "1rem"
   */
  padding?: string;
  background?: string;
  /**
   * @description Should receive a valid CSS string.
   * @example "1px solid #000000"
   */
  border?: string;
  $showPointer?: boolean;
}

interface TextTooltipProps extends ExtendedTooltipProps {
  tooltipText: string;
  textColor?: string;
  /**
   * @description Should receive a valid CSS string.
   * @example "1rem"
   */
  tooltipFontSize?: string;
  /**
   * @description Should receive a valid CSS string.
   * @example "1.5rem"
   */
  toolTipLineHeight?: string;
  textAlign?: 'start' | 'center' | 'end';
  /**
   * @description If true, the text will not wrap.
   */
  avoidWrappingText?: boolean;
}

type StyledTextTooltipProps = ThemeProp & TextTooltipProps & ExtendedTooltipProps;

const StyledTextTooltip = styled(Tooltip).attrs((props: StyledTextTooltipProps) => ({
  ...props,
  css: {
    ...props.css,
    marginTop: props.$noMarginTop ? '0' : 'auto',
    textAlign: props.textAlign || 'center',
    background: props.background || props.theme.colors.neutrals.c800,
    padding: props.padding || '0.25rem 0.5rem',
    borderRadius: '5px',
    border: props.border || 'none',
    fontSize: props.theme.fontVariants.mobile.body2.fontSize,
    lineHeight: props.theme.fontVariants.mobile.body2.lineHeight,
    color: props.textColor || props.theme.colors.content.white,
    '& .nextui-tooltip': {
      fontSize: props.tooltipFontSize || props.theme.fontSize.xs,
      lineHeight: props.toolTipLineHeight || '1rem',
      maxWidth: props.$maxWidth,
      minWidth: props.$minWidth,
      boxShadow: '0px 3px 8px 0px #27232C4D',
    },
    zIndex: props.$zIndex || ZIndexLayers.modal + 1,
    span: {
      background: props.theme.colors.neutrals.c800,
      marginBottom: '2px',
      borderRadius: 0,
      ...(props.placement === 'top' ? { bottom: '0px' } : {}),
      ...(props.placement === 'right' ? { left: '-2px' } : {}),
      ...(props.placement === 'bottom' ? { top: '2px' } : {}),
      ...(props.placement === 'left' ? { right: '-2px' } : {}),
    },
  },
}))<StyledTextTooltipProps>`
  min-width: 0%;
  max-width: 100%;
  ${(props) => !props.isDisabled && 'flex-shrink: 0;'}
  ${(props: ExtendedTooltipProps) => props.$showPointer && 'cursor: pointer'};
`;

const TextTooltip = ({ children, tooltipText, placement, avoidWrappingText, ...rest }: TextTooltipProps) => {
  return (
    <StyledTextTooltip
      content={<span style={{ whiteSpace: avoidWrappingText ? 'nowrap' : 'unset' }}>{tooltipText}</span>}
      placement={placement || 'top'}
      {...rest}
    >
      {children}
    </StyledTextTooltip>
  );
};

export default TextTooltip;
